// /console — every app you operate.
//
// This is the old "Your apps" page, promoted: it's now the aggregate view *of
// the console*, reachable from the landing's one CTA, rather than a
// customer-area page hanging off a nav link. Each card opens that project's own
// console in place (/console/{slug}) — the platform's own project included,
// because the platform is just another project.

import { Alert, Badge, Button, Card, CopyButton, Group, Loader, Stack, Text } from "@mantine/core";
import {
	IconCheck,
	IconCopy,
	IconExternalLink,
	IconPlus,
	IconRobot,
	IconSettings,
} from "@tabler/icons-react";
import { BASE_ENV_NAME } from "/sdk/index.js";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { c, font, radius } from "../../design/tokens.ts";
import { useAsync } from "../../hooks/useAsync.ts";
import { useProjectTree } from "../../hooks/useProjectTree.ts";
import type { ProjectManifest } from "../../lib/contracts.ts";
import { deployCommand, projectHost, projectPath, projectUrl } from "../../lib/hosts.ts";
import { useSession } from "../../session/SessionProvider.tsx";

// Past this age an in-flight provision record is a ghost, not a task. CreateApp
// gives the synchronous flow 6 minutes (PROVISION_TIMEOUT_MS) before declaring
// failure; a record older than that means the runtime never ran (a dead enqueue,
// a mid-deploy crash) and nothing will ever delete it — so offer a dismiss
// instead of spinning forever.
const STALE_AFTER_MS = 6 * 60 * 1000;

export function ConsoleHome() {
	const { client, me } = useSession();
	const projects = useProjectTree();

	// Projects mid-provisioning: inbox tasks the runtime hasn't finished (it
	// deletes the blob when done, so this is exactly the in-flight set).
	const inflight = useAsync<Array<{ taskId: string; slug: string; enqueuedAt: string }>>(
		async () => {
			if (!client || !me) return [];
			const tasks = await client.functions.listInflight({ function: "provision-project" });
			return tasks
				.filter((t) => t.enqueuedBy?.sub === me.sub)
				.map((t) => ({
					taskId: t.taskId,
					slug: (t.input as { slug?: string } | undefined)?.slug ?? "(unknown)",
					enqueuedAt: t.enqueuedAt,
				}));
		},
		[client, me?.sub],
	);

	const provisioning = inflight.data ?? [];
	// biome-ignore lint/correctness/useExhaustiveDependencies: poll only while something is in flight.
	useEffect(() => {
		if (provisioning.length === 0) return;
		const id = setInterval(() => {
			inflight.reload();
			projects.reload();
		}, 4000);
		return () => clearInterval(id);
	}, [provisioning.length]);

	return (
		<div style={{ maxWidth: 1120, margin: "0 auto", padding: "clamp(20px, 4vw, 36px) clamp(14px, 3vw, 32px) 72px" }}>
			<Group justify="space-between" align="flex-end" mb="xl" wrap="wrap" gap="md">
				<div>
					<h1
						style={{
							margin: "0 0 6px",
							fontSize: 30,
							fontWeight: 800,
							letterSpacing: "-0.03em",
						}}
					>
						Your apps
					</h1>
					<Text size="sm" c={c.muted}>
						Every project you operate. Open one to get its console — data, users, roles,
						functions, logs.
					</Text>
				</div>
				<Button component={Link} to="/signup" leftSection={<IconPlus size={16} />}>
					New app
				</Button>
			</Group>

			{provisioning.length > 0 && (
				<Stack gap="sm" mb="xl">
					{provisioning.map((p) => {
						// A record older than the provision timeout will never resolve on its
						// own: the runtime deletes it on completion, and provisioning takes
						// seconds, not minutes. Unparseable timestamps count as stalled too —
						// better a dismissible row than an eternal spinner.
						const age = Date.now() - Date.parse(p.enqueuedAt);
						const stalled = !(age < STALE_AFTER_MS);
						return stalled ? (
							<Card key={p.taskId} padding="md" radius="lg">
								<Group gap="xs">
									<Text ff={font.mono} size="sm">
										{p.slug}
									</Text>
									<Text size="sm" c={c.muted}>
										provisioning never finished — the task got no response from the
										runtime. Dismiss it and create the app again.
									</Text>
									<Badge color="yellow" variant="light" ml="auto">
										stalled
									</Badge>
									<Button
										size="xs"
										variant="default"
										onClick={async () => {
											await client?.functions.dismissInflight(p.taskId).catch(() => {});
											inflight.reload();
										}}
									>
										Dismiss
									</Button>
								</Group>
							</Card>
						) : (
							<Card key={p.taskId} padding="md" radius="lg">
								<Group gap="xs">
									<Loader size="xs" />
									<Text ff={font.mono} size="sm">
										{p.slug}
									</Text>
									<Text size="sm" c={c.muted}>
										provisioning — bucket, Cognito pool, IAM roles…
									</Text>
									<Badge color="blue" variant="light" ml="auto">
										in progress
									</Badge>
								</Group>
							</Card>
						);
					})}
				</Stack>
			)}

			{projects.loading ? (
				<Group justify="center" py="xl">
					<Loader />
				</Group>
			) : projects.error ? (
				<Alert color="red" variant="light" title="Couldn't list your apps">
					{projects.error}
				</Alert>
			) : (projects.data ?? []).length === 0 ? (
				<EmptyState />
			) : (
				<div
					className="s3p-cards-3"
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
						gap: 16,
					}}
				>
					{projects.roots.map((p) => (
						<AppCard
							key={p.slug}
							project={p}
							envs={projects.envs.get(p.slug) ?? []}
							candidate={projects.candidates.get(p.slug)}
							onLinked={() => projects.reload()}
						/>
					))}
					<NewAppCard />
				</div>
			)}
		</div>
	);
}

function AppCard({
	project: m,
	envs = [],
	candidate,
	onLinked,
}: {
	project: ProjectManifest;
	envs?: ProjectManifest[];
	candidate?: { base: string; environment: string };
	onLinked?: () => void;
}) {
	const { client } = useSession();
	const [linking, setLinking] = useState(false);
	const [linkError, setLinkError] = useState<string | null>(null);

	return (
		<Card padding="lg" radius="lg">
			<Stack gap="sm">
				<Group justify="space-between" wrap="nowrap">
					<Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
						<Text fw={700} fz={18} style={{ letterSpacing: "-0.02em" }} truncate>
							{m.name || m.slug}
						</Text>
						{/* The base project IS prod — but saying so on a project that has
						    no other environments would be noise, so the label only appears
						    once there's something to contrast it with. */}
						{envs.length > 0 && (
							<Badge size="xs" variant="light" color="green" style={{ flexShrink: 0 }}>
								{BASE_ENV_NAME}
							</Badge>
						)}
						{/* An environment whose base we can't see renders as its own card;
						    say what it is, or it looks like an unrelated project that
						    happens to be named `todo-dev`. */}
						{m.environmentOf && (
							<Badge size="xs" variant="light" color="orange" style={{ flexShrink: 0 }}>
								{m.environment} of {m.environmentOf}
							</Badge>
						)}
					</Group>
					<span
						style={{
							fontFamily: font.mono,
							fontSize: 10.5,
							color: c.blue,
							background: c.blueTint,
							border: `1px solid ${c.blueBorder}`,
							borderRadius: radius.pill,
							padding: "2px 9px",
							flexShrink: 0,
						}}
					>
						{m.slug}
					</span>
				</Group>

				<a
					href={projectUrl(m.slug)}
					target="_blank"
					rel="noreferrer"
					style={{
						fontFamily: font.mono,
						fontSize: 12.5,
						color: c.muted,
						textDecoration: "none",
					}}
				>
					{projectHost(m.slug)} ↗
				</a>

				<Group gap="xs" mt={4}>
					<Button
						component={Link}
						to={projectPath(m.slug)}
						size="xs"
						leftSection={<IconSettings size={14} />}
					>
						Console
					</Button>
					<Button
						component="a"
						href={projectUrl(m.slug)}
						target="_blank"
						size="xs"
						variant="light"
						leftSection={<IconExternalLink size={14} />}
					>
						Open app
					</Button>
					<CopyButton value={deployCommand(m.slug)}>
						{({ copied, copy }) => (
							<Button
								size="xs"
								variant="default"
								onClick={copy}
								leftSection={
									copied ? <IconCheck size={14} /> : <IconCopy size={14} />
								}
							>
								{copied ? "Copied" : "Deploy"}
							</Button>
						)}
					</CopyButton>
				</Group>

				{/* THE RETROFIT OFFER. A project named `acme-dev` next to a project
				    `acme` is almost always meant as its environment — but the link
				    is invisible until someone makes it, and nobody reads a doc to
				    discover a feature they don't know exists. So the console asks.
				    Nothing happens without the click, and it's reversible. */}
				{candidate && (
					<Stack gap={6} mt={2} pt="sm" style={{ borderTop: `1px solid ${c.blueBorder}` }}>
						<Text size="xs" c={c.muted}>
							Looks like the <b>{candidate.environment}</b> environment of{" "}
							<Text component="span" ff={font.mono} size="xs">
								{candidate.base}
							</Text>
							.
						</Text>
						{linkError && (
							<Text size="xs" c="red">
								{linkError}
							</Text>
						)}
						<Button
							size="compact-xs"
							variant="light"
							loading={linking}
							onClick={async () => {
								setLinking(true);
								setLinkError(null);
								try {
									await client?.projects?.linkEnvironment(
										candidate.base,
										candidate.environment,
									);
									onLinked?.();
								} catch (e) {
									setLinkError(e instanceof Error ? e.message : String(e));
								} finally {
									setLinking(false);
								}
							}}
						>
							Link as environment
						</Button>
					</Stack>
				)}

				{envs.length > 0 && (
					<Stack
						gap={6}
						mt={2}
						pt="sm"
						style={{ borderTop: `1px solid ${c.blueBorder}` }}
					>
						<Text size="xs" c={c.muted} fw={600}>
							Environments
						</Text>
						{envs.map((e) => (
							<Group key={e.slug} gap={8} wrap="nowrap" justify="space-between">
								<Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
									<Badge size="xs" variant="light" color="orange">
										{e.environment}
									</Badge>
									<Text ff={font.mono} size="xs" c={c.muted} truncate>
										{e.slug}
									</Text>
								</Group>
								<Button
									component={Link}
									to={projectPath(e.slug)}
									size="compact-xs"
									variant="subtle"
								>
									Console
								</Button>
							</Group>
						))}
					</Stack>
				)}
			</Stack>
		</Card>
	);
}

/** THE NEW-APP TILE — the one thing the grid is missing when you're done
 *  looking at what you have. It sits in the grid rather than only in the header
 *  because it's the same kind of object as its neighbours: a way in to an app.
 *
 *  It also carries the AGENT hand-off, which is the point: creating an app in
 *  the browser and delegating it to an agent are the same intent with two
 *  answers, so they belong on the same tile. The agent's setup lives on its own
 *  page (/agent) instead of expanded under the grid — it's a once-per-machine
 *  task, and rendering it on every visit made the home page look like a
 *  checklist. */
function NewAppCard() {
	return (
		<Card
			padding="lg"
			radius="lg"
			withBorder={false}
			style={{ border: `1px dashed ${c.borderStrong}`, background: "transparent" }}
		>
			<Stack gap="sm" h="100%">
				<Group gap={8} wrap="nowrap">
					<IconPlus size={17} color={c.blue} style={{ flexShrink: 0 }} />
					<Text fw={700} fz={18} style={{ letterSpacing: "-0.02em" }}>
						New app
					</Text>
				</Group>
				<Text size="sm" c={c.muted}>
					Pick a slug and provision it here, or hand the whole job to an AI agent — it
					creates the project, writes the app and deploys it.
				</Text>
				<Group gap="xs" mt="auto" pt={4}>
					<Button component={Link} to="/signup" size="xs" leftSection={<IconPlus size={14} />}>
						Create app
					</Button>
					<Button
						component={Link}
						to="/agent"
						size="xs"
						variant="light"
						leftSection={<IconRobot size={14} />}
					>
						Delegate to an agent
					</Button>
				</Group>
			</Stack>
		</Card>
	);
}

function EmptyState() {
	return (
		<Card padding="xl" radius="lg">
			<Stack align="center" gap="sm" py="lg">
				<Text fw={700} fz={20} style={{ letterSpacing: "-0.02em" }}>
					No apps yet.
				</Text>
				<Text size="sm" c={c.muted} ta="center" maw={460}>
					Create one yourself in the browser, or connect an agent — hand your agent code
					to Claude Code (or any agent with a shell) and ask it to build something; it
					creates the project and deploys it for you.
				</Text>
				<Group gap="xs" mt="xs">
					<Button component={Link} to="/signup" leftSection={<IconPlus size={16} />}>
						Create an app
					</Button>
					<Button
						component={Link}
						to="/agent"
						variant="light"
						leftSection={<IconRobot size={16} />}
					>
						Connect an agent
					</Button>
				</Group>
			</Stack>
		</Card>
	);
}
